import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RotateCcw, Save, X } from 'lucide-react';

/**
 * Panel zum Anzeigen und Bearbeiten der KI-System-Regeln.
 */
export default function AiChatRulesPanel({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [sections, setSections] = useState([]);
  const [customInstructions, setCustomInstructions] = useState('');
  const [safetyLock, setSafetyLock] = useState('');
  const [limits, setLimits] = useState({ maxSectionChars: 4000, maxCustomChars: 6000 });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setOk('');
    (async () => {
      try {
        const res = await axios.get('/api/ai/chat-rules');
        if (cancelled) return;
        if (!res.data?.success) throw new Error(res.data?.error || 'Laden fehlgeschlagen');
        const rules = res.data.rules;
        setSections(Array.isArray(rules.sections) ? rules.sections : []);
        setCustomInstructions(String(rules.customInstructions || ''));
        setSafetyLock(String(rules.safetyLock || ''));
        if (rules.limits) setLimits(rules.limits);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Fehler');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const updateSection = (id, patch) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch, isDefault: false } : s)));
    setOk('');
  };

  const resetSection = (id) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, enabled: true, text: s.defaultText, isDefault: true }
          : s
      )
    );
    setOk('');
  };

  const resetAll = () => {
    setSections((prev) =>
      prev.map((s) => ({ ...s, enabled: true, text: s.defaultText, isDefault: true }))
    );
    setCustomInstructions('');
    setOk('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setOk('');
    try {
      const payload = {
        sections: Object.fromEntries(
          sections.map((s) => [s.id, { enabled: !!s.enabled, text: s.text }])
        ),
        customInstructions,
      };
      const res = await axios.put('/api/ai/chat-rules', { rules: payload });
      if (!res.data?.success) throw new Error(res.data?.error || 'Speichern fehlgeschlagen');
      const rules = res.data.rules;
      setSections(Array.isArray(rules.sections) ? rules.sections : []);
      setCustomInstructions(String(rules.customInstructions || ''));
      setOk('Gespeichert — gilt für die nächsten Chat-/Agent-Anfragen');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const chatSections = sections.filter((s) => s.group === 'chat' || s.group === 'both');
  const agentSections = sections.filter((s) => s.group === 'agent');

  return (
    <div className="fixed inset-0 z-[120] flex items-stretch justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-neutral-950/50 backdrop-blur-[2px]"
        aria-label="Schließen"
        onClick={onClose}
      />
      <div className="relative flex h-full w-full max-w-lg flex-col border-l border-neutral-200 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900 scale-in">
        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">KI-Regeln</h2>
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
              System-Prompt bearbeiten · pro Account gespeichert
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 py-10 justify-center text-sm text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Lade Regeln…
            </div>
          ) : (
            <>
              {safetyLock ? (
                <div className="rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-[11px] leading-relaxed text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                  <span className="font-semibold">Immer aktiv (nicht änderbar): </span>
                  {safetyLock}
                </div>
              ) : null}

              {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
                  {error}
                </div>
              ) : null}
              {ok ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200">
                  {ok}
                </div>
              ) : null}

              <section className="space-y-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                  Chat-Modus
                </h3>
                {chatSections.map((sec) => (
                  <RuleEditor
                    key={sec.id}
                    section={sec}
                    maxChars={limits.maxSectionChars}
                    onChange={(patch) => updateSection(sec.id, patch)}
                    onReset={() => resetSection(sec.id)}
                  />
                ))}
              </section>

              <section className="space-y-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                  Agent-Modus
                </h3>
                {agentSections.map((sec) => (
                  <RuleEditor
                    key={sec.id}
                    section={sec}
                    maxChars={limits.maxSectionChars}
                    onChange={(patch) => updateSection(sec.id, patch)}
                    onReset={() => resetSection(sec.id)}
                  />
                ))}
              </section>

              <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                    Eigene Zusatzregeln
                  </h3>
                  <span className="text-[10px] tabular-nums text-neutral-400">
                    {customInstructions.length}/{limits.maxCustomChars}
                  </span>
                </div>
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                  Werden zusätzlich an den System-Prompt gehängt (Chat und Agent).
                </p>
                <textarea
                  value={customInstructions}
                  onChange={(e) => {
                    setCustomInstructions(e.target.value.slice(0, limits.maxCustomChars));
                    setOk('');
                  }}
                  rows={5}
                  placeholder="z. B. Antworte immer duzend · Bevorzuge kurze Bullet-Points …"
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-[12px] leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
                />
              </section>
            </>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-neutral-200 px-4 py-3 dark:border-neutral-800">
          <button
            type="button"
            onClick={resetAll}
            disabled={loading || saving}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium text-neutral-600 hover:bg-neutral-100 disabled:opacity-40 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Alles zurücksetzen
          </button>
          <button
            type="button"
            onClick={save}
            disabled={loading || saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-neutral-800 disabled:opacity-40 dark:bg-emerald-600 dark:hover:bg-emerald-500"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function RuleEditor({ section, maxChars, onChange, onReset }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-950/50">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[12px] font-semibold text-neutral-900 dark:text-neutral-100">
              {section.title}
            </span>
            {!section.isDefault ? (
              <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
                geändert
              </span>
            ) : null}
          </div>
          {section.description ? (
            <p className="mt-0.5 text-[10px] text-neutral-500 dark:text-neutral-400">
              {section.description}
            </p>
          ) : null}
        </div>
        <label className="flex flex-shrink-0 items-center gap-1.5 text-[11px] text-neutral-600 dark:text-neutral-300">
          <input
            type="checkbox"
            checked={!!section.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          An
        </label>
      </div>
      <textarea
        value={section.text}
        disabled={!section.enabled}
        onChange={(e) => onChange({ text: e.target.value.slice(0, maxChars) })}
        rows={4}
        className="mt-2 w-full rounded-lg border border-neutral-200 bg-white px-2.5 py-2 font-mono text-[11px] leading-relaxed text-neutral-800 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-45 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
      />
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="text-[10px] tabular-nums text-neutral-400">
          {(section.text || '').length}/{maxChars}
        </span>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 text-[10px] font-medium text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
        >
          <RotateCcw className="h-3 w-3" />
          Standard
        </button>
      </div>
    </div>
  );
}
